import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import template from './color-settings.hbs'
import FormView from 'scoreboard/components/form-view'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import ColorPicker from 'scoreboard/components/color-picker'
import * as colors from 'scoreboard/charts/config/chart-colors'
import { ColorTypes, DEFAULT_COLOR_TYPE } from '../color-types'
import enums from 'scoreboard/components/enums'

const ColorSettings = Backbone.View.extend({
	tagName: 'form',

	className: 'color-settings',

	/**
	 * @type ChartDesigner
	 */
	chartDesigner: null,

	/**
	 * @param {ChartDesigner} chartDesigner
	 * @param {Object} [options.setting]
	 */
	initialize: function(options) {
		this.chartDesigner = options.chartDesigner
		this.setting = _.clone(options.setting)
		this.specificColor = this.setting.specificColor

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			test: () => this.test(),
			// The color setting defaults to manual, so no changes are needed
			allowCleanSubmission: true
		})
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.renderToggle()
		this.renderColorPicker()
		this.formView.start()
		this.onColorTypeChanged(this.setting.colorType || DEFAULT_COLOR_TYPE)
		return this
	},

	context: function() {
		const plotType = this.setting.plotType
		if ([ 'line', 'area' ].includes(plotType)) {
			return { title: i18n.get(`charts.chart.color.setColors.${plotType}`) }
		}
		// #41614 correct dialog title for pie and donut slice colors
		if (['pie','donut'].includes(plotType)) {
			return { title: i18n.get('charts.chart.color.setColors', `charts.chart.plotType.${plotType}.slice`) }
		}
		return {
			title: i18n.get('charts.chart.color.setColors',
				`charts.chart.plotType.${_.camelCase(plotType)}`)
		}
	},

	renderToggle: function() {
		const toggleData = enums.toToggleData('chartColorType')
		const plotType = this.setting.plotType

		// conditional colors will be implemented in #36709
		_.remove(toggleData, d => d.value === 'conditional')

		if (!this.chartDesigner.supportsScorePointColor()) {
			// Only scorecards and initiatives have score/performance colors,
			// so don't show that option if no scorecard/initiative serie
			_.remove(toggleData, d => d.value === 'score')
		}

		if ([ 'line', 'area' ].includes(plotType)) {
			// #40220 use custom text for the 'score' option for lines #40306 and areas
			const scoreOption = _.find(toggleData, d => d.value === 'score')
			_.set(scoreOption, 'label', i18n.get(`charts.chart.colorType.score.${plotType}`))
		}

		this.toggle = new LabeledToggle({ options: toggleData })

		this.$('.toggle-container').append(this.toggle.el)
		this.toggle.render()
		// Make sure the toggle shows whatever was last selected
		this.toggle.val(this.setting.colorType || DEFAULT_COLOR_TYPE, { silent: false })
		this.toggle.on('change', this.onColorTypeChanged.bind(this))
	},

	renderColorPicker: function() {
		this.colorPicker = new ColorPicker({
			el: this.$('.color-picker'),
			colors: colors.grouped()
		})

		this.colorPicker.render()
		this.colorPicker.color(this.setting.specificColor)

		this.colorPicker.on('select', color => {
			this.setting.specificColor = color
			this.colorPicker.color(color)
			this.formView.setDirty()
		})
	},

	onColorTypeChanged: function(colorType) {
		this.setting.colorType = colorType
		this.$('.color-picker-container')
			.toggleClass('is-not-displayed', colorType != ColorTypes.SPECIFIC)
		if (colorType != ColorTypes.SPECIFIC) {
			this.setting.specificColor = null
		}
	},

	test: function() {
		if (this.setting.colorType === ColorTypes.SPECIFIC && !this.setting.specificColor) {
			return i18n.get('charts.chart.colorType.specificColorRequired')
		}
	},

	submit: function() {
		this.trigger('done', this.setting)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.toggle, this.colorPicker], 'remove')
	}
})

export default ColorSettings
ColorSettings.__filename__ = 'charts/edit/color-settings.js'
